import { ADDABLE_NODE_TEMPLATES } from "./nodeTemplates";
import { PALETTE_DRAG_TYPE } from "./FlowCanvas";
import { nodeTypeStyle } from "./nodeTypeStyle";

interface PaletteProps {
  onAddNode: (type: string, config: unknown) => void;
}

// Fixed, deliberate order (not alphabetical): roughly how a flow is
// usually authored — say something, collect input, decide/repeat/end,
// then the less-common compliance/data/integration steps.
const CATEGORY_ORDER = ["Messaging", "Input", "Flow control", "Compliance", "Data", "Integration"];

/**
 * Lists every addable node type (nodeTemplates.ts), grouped by
 * nodeTypeStyle.ts's own category with a matching color swatch — purely a
 * scanning aid, the same category/color a node gets on the canvas once
 * added. Clicking one appends a new, disconnected node to the canvas with
 * a schema-shaped default config, selected immediately so the Inspector
 * opens on it; dragging one onto the canvas adds it where it is dropped. `start` and `amd_gate` are deliberately absent — see
 * nodeTemplates.ts's own comment for why.
 */
export function Palette({ onAddNode }: PaletteProps) {
  const groups = CATEGORY_ORDER.map((category) => ({
    category,
    templates: ADDABLE_NODE_TEMPLATES.filter((t) => nodeTypeStyle(t.type).category === category),
  })).filter((group) => group.templates.length > 0);

  return (
    <aside className="card palette" aria-labelledby="palette-heading">
      <h2 id="palette-heading">Add node</h2>
      {groups.map((group) => (
        <div key={group.category} className="palette__group">
          <p className="palette__group-label">{group.category}</p>
          <ul className="palette__list">
            {group.templates.map((template) => (
              <li key={template.type}>
                <button
                  type="button"
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData(PALETTE_DRAG_TYPE, template.type);
                    event.dataTransfer.effectAllowed = "move";
                  }}
                  onClick={() => onAddNode(template.type, template.defaultConfig())}
                >
                  <span
                    className="palette__swatch"
                    style={{ background: nodeTypeStyle(template.type).color }}
                    aria-hidden="true"
                  />
                  {template.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="card__note">Click to add, or drag onto the canvas. A new step starts disconnected.</p>
    </aside>
  );
}
